import { useEffect, useRef, useState } from 'react';
import { OFF } from '../lib/off';
import { Link, useLocation } from 'react-router';
import type { ChangeSetView, PlanWhen } from '../api/client';
import { Icon } from '../lib/icons';
import { suggestionFor, useLeland, type LelandEntry, type PlanState } from '../lib/leland';
import { clockTime } from '../lib/format';
import { renderMarkdown } from '../help';
import { Button, Chip } from './ui';
import { ImportCard } from './ImportCard';
// Changes later, or for a while (1.12.15).
import { ScheduledList, laterText } from './Scheduled';

const SUGGESTIONS = ['Who is in the Support queue?', 'Add our new hire to the front desk ring group', 'Close the office early on Christmas Eve', 'Where does our main number go after hours?'];

/** The conversation: what Leland said, its questions and plans, and the box to ask. */
export function LelandPanel({ variant, onClose, base }: { variant: 'float' | 'page'; onClose?: () => void; base: string }) {
  const l = useLeland();
  const [text, setText] = useState('');
  const list = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLTextAreaElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (l?.draft) {
      setText(l.draft);
      l.setDraft('');
      window.setTimeout(() => box.current?.focus(), 0);
    }
  }, [l]);
  useEffect(() => {
    list.current?.scrollTo({ top: list.current.scrollHeight, behavior: 'smooth' });
  }, [l?.entries.length, l?.busy]);
  if (!l) return null;
  const send = (t: string) => {
    setText('');
    void l.send(t);
  };
  // The latest question is the one to answer.
  const lastQuestion = [...l.entries].reverse().find((e) => e.from === 'leland' && 'item' in e && e.item.type === 'question');

  return (
    <section className={`leland ${variant}`} aria-label="Leland">
      <header className="leland-head">
        <span className="leland-mark" aria-hidden="true">
          <Icon name="spark" size={16} />
        </span>
        <span className="cell-stack grow">
          <strong>Leland</strong>
          <small className="muted">Tell it what to change. You approve every change, and you can undo it.</small>
        </span>
        {l.entries.length > 0 && (
          <button type="button" className="link-btn" onClick={l.reset}>
            New chat
          </button>
        )}
        {onClose && (
          <button type="button" className="icon-btn" aria-label="Close Leland" onClick={onClose}>
            <Icon name="x" size={17} />
          </button>
        )}
      </header>

      <div className="leland-body" ref={list} aria-live="polite">
        {l.enabled === false ? (
          <div className="leland-empty">
            <p>
              <strong>Leland isn’t set up on Stitch yet.</strong>
            </p>
            <p className="muted">{OFF.lelandNotSetUp} Until then, everything works the usual way.</p>
          </div>
        ) : l.entries.length === 0 ? (
          <div className="leland-empty">
            <p>Ask in your own words. Leland looks at your phone system, asks if anything’s unclear, and shows you a plan before anything changes.</p>
            <p className="muted small">Adding a lot of people? Attach a spreadsheet (Excel or CSV) with the paper clip below.</p>
            <div className="leland-suggest">
              {SUGGESTIONS.map((s) => (
                <button key={s} type="button" className="preset" onClick={() => send(s)}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          l.entries.map((e) => <Entry key={e.id} e={e} answerable={e === lastQuestion && !l.busy} onAnswer={send} base={base} />)
        )}
        {l.busy && (
          <div className="leland-msg leland-from">
            <span className="leland-typing" aria-label="Leland is working">
              <span />
              <span />
              <span />
            </span>
          </div>
        )}
      </div>

      {l.enabled !== false && (
        <form
          className="leland-ask"
          onSubmit={(e) => {
            e.preventDefault();
            send(text);
          }}
        >
          <textarea
            ref={box}
            className="input"
            rows={2}
            maxLength={4000}
            value={text}
            placeholder="Ask Leland to change something"
            aria-label="Ask Leland"
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send(text);
              }
            }}
          />
          <div className="leland-ask-actions">
            <button type="button" className="icon-btn" aria-label="Attach a spreadsheet of people" title="Attach a spreadsheet of people (Excel or CSV)" disabled={l.busy} onClick={() => picker.current?.click()}>
              <Icon name="upload" size={18} strokeWidth={2.1} />
            </button>
            <input
              ref={picker}
              type="file"
              hidden
              accept=".xlsx,.csv,.tsv,.txt,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
              aria-label="Spreadsheet of people"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (!f) return;
                const said = text;
                setText('');
                void l.attach(f, said);
              }}
            />
            <Button variant="primary" icon="send" type="submit" disabled={!text.trim() || l.busy} aria-label="Send">
              Send
            </Button>
          </div>
        </form>
      )}
      <p className="leland-foot muted">Leland can only change what your access allows. Getting numbers and 911 addresses are on Numbers and Emergency addresses.</p>
    </section>
  );
}

function Entry({ e, answerable, onAnswer, base }: { e: LelandEntry; answerable: boolean; onAnswer: (t: string) => void; base: string }) {
  const l = useLeland();
  if (e.from === 'me')
    return (
      <div className="leland-msg leland-me">
        {e.file && (
          <span className="leland-file">
            <Icon name="upload" size={14} strokeWidth={2.2} /> {e.file}
          </span>
        )}
        {e.text}
      </div>
    );
  if ('imp' in e) return <LelandImport entryId={e.id} base={base} />;
  const item = e.item;
  // Leland writes Markdown (lists, bold, tables); shown with the help center's safe rules (no raw HTML).
  if (item.type === 'text') return <div className="leland-msg leland-from leland-md" dangerouslySetInnerHTML={{ __html: renderMarkdown(item.text) }} />;
  if (item.type === 'error')
    return (
      <div className="leland-msg leland-from leland-error" role="alert">
        {item.text}
      </div>
    );
  if (item.type === 'question')
    return (
      <div className="leland-card">
        <strong>{item.question}</strong>
        <div className="leland-choices">
          {item.choices.map((c) => (
            <button key={c.label} type="button" className="leland-choice" disabled={!answerable} onClick={() => onAnswer(c.label)}>
              <span>{c.label}</span>
              {c.detail && <small className="muted">{c.detail}</small>}
            </button>
          ))}
        </div>
      </div>
    );
  if (item.type === 'scheduled' && l)
    return (
      <div className="leland-card">
        <strong>Scheduled</strong>
        <ScheduledList domain={l.domain} initial={item.scheduled} />
      </div>
    );
  return 'state' in e && item.type === 'plan' ? <PlanCard entryId={e.id} cs={item.changeSet} when={item.when} state={e.state} base={base} /> : null;
}

function LelandImport({ entryId, base }: { entryId: number; base: string }) {
  const l = useLeland();
  const e = l?.entries.find((x) => x.id === entryId);
  if (!l || !e || !('imp' in e)) return null;
  return <ImportCard domain={l.domain} state={e.imp} update={(fn) => l.updateImport(entryId, fn)} viaLeland base={base} />;
}

function PlanCard({ entryId, cs, when, state, base }: { entryId: number; cs: ChangeSetView; when?: PlanWhen | undefined; state: PlanState; base: string }) {
  const l = useLeland();
  if (!l) return null;
  const n = cs.steps.length;
  const result = 'result' in state ? state.result : undefined;
  const putBack = state.phase === 'applied' ? state.putBack : undefined;
  return (
    <div className={`leland-card plan ${state.phase}`}>
      <div className="plan-head">
        <strong>
          {state.phase === 'applied' || state.phase === 'undoing'
            ? 'Done'
            : state.phase === 'undone'
              ? putBack === undefined && when?.kind === 'until'
                ? 'Put back'
                : 'Undone'
              : state.phase === 'scheduled'
                ? `Scheduled for ${when?.text ?? laterText(state.scheduled.runAt)}`
                : `Here’s the plan · ${n} ${n === 1 ? 'change' : 'changes'}`}
        </strong>
        {state.phase === 'applied' && <Chip small tone="ok">Applied {result?.appliedAt ? clockTime(result.appliedAt) : ''}</Chip>}
        {state.phase === 'discarded' && <Chip small>Not applied</Chip>}
      </div>
      <ul className="plan-steps">
        {(result ?? cs).steps.map((s) => (
          <li key={s.id} className={s.state}>
            <Icon name={state.phase === 'applied' || state.phase === 'undoing' ? 'check' : 'chevRight'} size={14} />
            <span>{s.summary}</span>
          </li>
        ))}
      </ul>
      {cs.notes.length > 0 && state.phase === 'planned' && (
        <ul className="plan-notes">
          {cs.notes.map((note) => (
            <li key={note} className="muted">
              {note}
            </li>
          ))}
        </ul>
      )}
      {state.phase === 'planned' && when && (
        <p className="plan-when">
          <Icon name="clock" size={15} />
          <span>
            {when.kind === 'until' ? (
              <>
                Now, and put back by itself at <b>{when.text}</b>: {when.summary}.
              </>
            ) : (
              <>
                Nothing changes now. At <b>{when.text}</b>: {when.summary}.
              </>
            )}
          </span>
        </p>
      )}
      {state.phase === 'planned' && (
        <>
          <p className="muted small">
            {when?.kind === 'at'
              ? 'It runs as you, if you can still make the change then. You can cancel it on Change history until then.'
              : 'Nothing changes until you apply. Everything except emails can be undone in one step.'}
          </p>
          <div className="plan-actions">
            <Button variant="primary" onClick={() => void l.apply(entryId)}>
              {when?.kind === 'at' ? `Schedule for ${when.text}` : when?.kind === 'until' ? `Apply until ${when.text}` : `Apply ${n} ${n === 1 ? 'change' : 'changes'}`}
            </Button>
            <Button variant="ghost" onClick={() => void l.discard(entryId)}>
              Not now
            </Button>
          </div>
        </>
      )}
      {state.phase === 'applying' && <p className="muted small">Applying, and checking each change on the phone system…</p>}
      {state.phase === 'scheduled' && (
        <>
          <p className="muted small">Nothing has changed yet. It runs at {when?.text ?? laterText(state.scheduled.runAt)}, as you. You’ll get a notification when it has.</p>
          <div className="plan-actions">
            <Button variant="secondary" onClick={() => void l.later(entryId, 'run')}>
              Do it now
            </Button>
            <Button variant="ghost" onClick={() => void l.later(entryId, 'cancel')}>
              Cancel
            </Button>
          </div>
        </>
      )}
      {state.phase === 'applied' && putBack && (
        <p className="plan-when">
          <Icon name="clock" size={15} />
          <span>
            Puts back by itself at <b>{laterText(putBack.runAt)}</b>: {putBack.summary}.
          </span>
        </p>
      )}
      {state.phase === 'applied' && (
        <>
          <p className="muted small">Each change was checked on the phone system after it was applied. Logged as Leland, for you.</p>
          <div className="plan-actions">
            {putBack ? (
              <>
                <Button variant="secondary" icon="undo" onClick={() => void l.later(entryId, 'run')}>
                  Put back now
                </Button>
                <Button variant="ghost" onClick={() => void l.later(entryId, 'cancel')}>
                  Keep it this way
                </Button>
              </>
            ) : (
              <Button variant="secondary" icon="undo" onClick={() => void l.undo(entryId)}>
                Undo all
              </Button>
            )}
            <Link className="link-btn" to={`${base}/history`}>
              Change history
            </Link>
          </div>
        </>
      )}
      {state.phase === 'undoing' && <p className="muted small">Undoing…</p>}
      {state.phase === 'undone' && <p className="muted small">Everything is back to how it was.</p>}
      {state.phase === 'failed' && (
        <p className="crit-text small" role="alert">
          {state.message}
        </p>
      )}
    </div>
  );
}

/**
 * The round button in the corner of every company page, and the nudge it shows when someone
 * starts a change by hand: they could just ask Leland.
 */
export function LelandLauncher({ base }: { base: string }) {
  const l = useLeland();
  const { pathname } = useLocation();
  const [nudge, setNudge] = useState<{ title: string; ask: string } | null>(null);
  useEffect(() => {
    const onEditing = (ev: Event) => {
      const title = (ev as CustomEvent<{ title: string }>).detail?.title ?? '';
      if (!l?.enabled || l.open || snoozed()) return;
      setNudge({ title, ask: suggestionFor(title) });
    };
    window.addEventListener('stitch:editing', onEditing);
    return () => window.removeEventListener('stitch:editing', onEditing);
  }, [l]);
  useEffect(() => {
    if (!nudge) return;
    const t = window.setTimeout(() => setNudge(null), 12_000);
    return () => window.clearTimeout(t);
  }, [nudge]);
  // The Leland and Get set up pages have the conversation already.
  if (!l?.enabled || /\/(leland|setup)$/.test(pathname.replace(/\/+$/, ''))) return null;
  const openWith = (draft: string) => {
    // Whatever dialog was open steps aside for Leland.
    window.dispatchEvent(new Event('stitch:close-overlays'));
    if (draft) l.setDraft(draft);
    l.setOpen(true);
    setNudge(null);
  };
  return (
    <>
      {nudge && !l.open && (
        <div className="leland-nudge" role="status">
          <span className="leland-mark" aria-hidden="true">
            <Icon name="spark" size={14} />
          </span>
          <span className="grow">
            <strong>You can just ask Leland.</strong> Say what you want in your own words, and it sets it up for you to check.
          </span>
          <span className="nudge-actions">
            <Button size="small" variant="primary" onClick={() => openWith(nudge.ask)}>
              Ask Leland
            </Button>
            <button
              type="button"
              className="link-btn"
              onClick={() => {
                snooze();
                setNudge(null);
              }}
            >
              Not now
            </button>
          </span>
        </div>
      )}
      {l.open ? (
        <div className="leland-float" role="dialog" aria-label="Leland">
          <LelandPanel variant="float" onClose={() => l.setOpen(false)} base={base} />
        </div>
      ) : (
        <button type="button" className="leland-fab" aria-label="Ask Leland" title="Ask Leland" onClick={() => openWith('')}>
          <Icon name="spark" size={22} />
        </button>
      )}
    </>
  );
}

const SNOOZE_KEY = 'stitch.leland.nudge';
function snoozed(): boolean {
  try {
    return Number(localStorage.getItem(SNOOZE_KEY) ?? 0) > Date.now();
  } catch {
    return false;
  }
}
function snooze(): void {
  try {
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + 24 * 3600_000));
  } catch {
    // Private mode: it just shows again next time.
  }
}
